import { Button } from '../ui/Button';
import { Spinner } from '../Spinner';
import { COLORS } from '../../constants/theme';
import { isProtected, formatProtectionCountdown } from '../../utils/protection';
import type { KingdomMember } from '../../types/magic';

interface TargetSelectorProps {
  kingdomNumber: string;
  islandNumber: string;
  selectedTarget: string;
  kingdomMembers: KingdomMember[];
  isLoadingKingdom: boolean;
  isQuickIntelLoading: string | null;
  hasTarget: boolean;
  currentTick: number;
  onKingdomChange: (kingdom: string) => void;
  onIslandChange: (island: string) => void;
  onLoadKingdom: () => void;
  onTargetChange: (targetId: string) => void;
  onQuickIntel: (type: 'CB' | 'SOM' | 'SURVEY' | 'SOS' | 'SOHD' | 'SOT') => void;
}

export function TargetSelector({
  kingdomNumber,
  islandNumber,
  selectedTarget,
  kingdomMembers,
  isLoadingKingdom,
  isQuickIntelLoading,
  hasTarget,
  currentTick,
  onKingdomChange,
  onIslandChange,
  onLoadKingdom,
  onTargetChange,
  onQuickIntel,
}: TargetSelectorProps) {
  const selectedMember = kingdomMembers.find((m) => m.provinceId === selectedTarget) ?? null;
  const selectedProtectionCountdown = selectedMember
    ? formatProtectionCountdown(selectedMember.protectionExpiresAtTick, currentTick)
    : null;
  return (
    <div className="rounded" style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}`, padding: '1rem' }}>
      <h2 className="text-lg font-semibold mb-3" style={{ color: COLORS.text.primary }}>Select Target</h2>

      {/* Row 1: Kingdom, Island, Load Kingdom */}
      <div className="flex gap-2 items-end">
        <div className="w-20">
          <label className="block text-xs mb-1" style={{ color: COLORS.text.tertiary }}>Kingdom</label>
          <input
            type="number"
            min="1"
            max="40"
            value={kingdomNumber}
            onChange={(e) => onKingdomChange(e.target.value)}
            className="w-full px-2 py-1.5 rounded text-sm"
            style={{ background: COLORS.inputBg, border: `1px solid ${COLORS.inputBorder}`, color: COLORS.text.primary }}
            placeholder="1-40"
          />
        </div>
        <div className="w-20">
          <label className="block text-xs mb-1" style={{ color: COLORS.text.tertiary }}>Island</label>
          <input
            type="number"
            min="1"
            max="25"
            value={islandNumber}
            onChange={(e) => onIslandChange(e.target.value)}
            className="w-full px-2 py-1.5 rounded text-sm"
            style={{ background: COLORS.inputBg, border: `1px solid ${COLORS.inputBorder}`, color: COLORS.text.primary }}
            placeholder="1-25"
          />
        </div>
        <Button
          onClick={onLoadKingdom}
          disabled={isLoadingKingdom || !islandNumber || !kingdomNumber}
          variant="secondary"
          className="text-sm px-3 py-1.5"
        >
          {isLoadingKingdom ? <Spinner size="sm" /> : 'Load Kingdom'}
        </Button>
      </div>

      {/* Row 2: Target Province, full width — no longer competing for space with the numeric
          inputs, so the currently-selected province name is actually legible. */}
      <div className="mt-2">
        <label className="block text-xs mb-1" style={{ color: COLORS.text.tertiary }}>Target Province</label>
        <select
          value={selectedTarget}
          onChange={(e) => onTargetChange(e.target.value)}
          disabled={kingdomMembers.length === 0}
          className="w-full px-2 py-1.5 rounded text-sm"
          style={
            kingdomMembers.length === 0
              ? { background: COLORS.cardAlt, border: `1px solid ${COLORS.border}`, color: COLORS.text.muted, cursor: 'not-allowed' }
              : { background: COLORS.inputBg, border: `1px solid ${COLORS.inputBorder}`, color: COLORS.text.primary }
          }
        >
          {kingdomMembers.length === 0 ? (
            <option value="">Load a kingdom first</option>
          ) : (
            <>
              <option value="">Select a province...</option>
              {kingdomMembers.map((member) => {
                const memberProtected = isProtected(member.protectionExpiresAtTick, currentTick);
                return (
                  <option key={member.provinceId} value={member.provinceId} disabled={memberProtected}>
                    {member.provinceName} ({member.location}){memberProtected ? ' (Protected)' : ''}
                  </option>
                );
              })}
            </>
          )}
        </select>
        {selectedProtectionCountdown && (
          <div className="text-xs mt-1" style={{ color: COLORS.link }}>
            {selectedProtectionCountdown} — this target cannot be attacked yet.
          </div>
        )}
      </div>

      {/* Quick Intel */}
      {hasTarget && (
        <div className="flex flex-wrap items-center gap-2 mt-3 pt-3" style={{ borderTop: `1px solid ${COLORS.border}` }}>
          <span className="text-xs" style={{ color: COLORS.text.tertiary }}>Quick Intel:</span>
          <Button
            onClick={() => onQuickIntel('SOHD')}
            disabled={isQuickIntelLoading !== null}
            variant="secondary"
            className="text-xs py-1 px-2"
            style={{ minHeight: 44 }}
          >
            {isQuickIntelLoading === 'SOHD' ? <Spinner size="sm" /> : 'Spy on Home Defense'}
          </Button>
          <Button
            onClick={() => onQuickIntel('CB')}
            disabled={isQuickIntelLoading !== null}
            variant="secondary"
            className="text-xs py-1 px-2"
            style={{ minHeight: 44 }}
          >
            {isQuickIntelLoading === 'CB' ? <Spinner size="sm" /> : 'Crystal Ball'}
          </Button>
          <Button
            onClick={() => onQuickIntel('SOM')}
            disabled={isQuickIntelLoading !== null}
            variant="secondary"
            className="text-xs py-1 px-2"
            style={{ minHeight: 44 }}
          >
            {isQuickIntelLoading === 'SOM' ? <Spinner size="sm" /> : 'Spy on Military'}
          </Button>
          <Button
            onClick={() => onQuickIntel('SURVEY')}
            disabled={isQuickIntelLoading !== null}
            variant="secondary"
            className="text-xs py-1 px-2"
            style={{ minHeight: 44 }}
          >
            {isQuickIntelLoading === 'SURVEY' ? <Spinner size="sm" /> : 'Survey'}
          </Button>
          <Button
            onClick={() => onQuickIntel('SOS')}
            disabled={isQuickIntelLoading !== null}
            variant="secondary"
            className="text-xs py-1 px-2"
            style={{ minHeight: 44 }}
          >
            {isQuickIntelLoading === 'SOS' ? <Spinner size="sm" /> : 'Spy on Sciences'}
          </Button>
          <Button
            onClick={() => onQuickIntel('SOT')}
            disabled={isQuickIntelLoading !== null}
            variant="secondary"
            className="text-xs py-1 px-2"
            style={{ minHeight: 44 }}
          >
            {isQuickIntelLoading === 'SOT' ? <Spinner size="sm" /> : 'Spy on Throne'}
          </Button>
        </div>
      )}
    </div>
  );
}
